import * as React from 'react';
import * as styles from './BoardMenuItem.scss';
import { observer } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon } from '@fluentui/react/lib/Icon';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { ControlIcons } from 'owa-control-icons';
import {
    getProject,
    openTimeStreamProject,
    closeCurrentTimeStreamProject,
} from 'owa-timestream-store';
import { useConfirmAndDeleteOutlookSpace } from 'owa-timestream-projectutils';
import { getDefaultBoardProject } from 'owa-calendar-board-projects';
import { lazyOpenBoardEditorDialog } from 'owa-calendar-board-editor';
import loc from 'owa-localize';
import { editBoardAriaLabel, deleteBoardAriaLabel } from './BoardMenuItem.locstring.json';

interface BoardMenuItemProps extends IContextualMenuItem {
    dismissMenu: (ev?: any, dismissAll?: boolean) => void;
}

export default observer(function BoardMenuItem(props: BoardMenuItemProps) {
    const {
        text,
        title,
        checked,
        onClick,
        data: { projectId },
        dismissMenu,
    } = props;

    const [shouldShowEditTools, showEditTools, hideEditTools] = useBooleanState(false);

    const onBoardClicked = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            onClick?.();
            // close project first to handle any state cleanup needed
            closeCurrentTimeStreamProject();
            openTimeStreamProject(projectId);
            dismissMenu(ev, true /* dismissAll */);
        },
        [projectId, onClick, dismissMenu]
    );

    const onEditBoardClicked = React.useCallback(
        (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            if (projectId && text) {
                lazyOpenBoardEditorDialog.importAndExecute({ projectId, name: text });
            }
        },
        [projectId, text]
    );

    const defaultProject = getDefaultBoardProject();
    const onProjectDeleted = React.useCallback(() => {
        if (checked && defaultProject) {
            openTimeStreamProject(defaultProject.projectId);
        }
    }, [checked, defaultProject?.projectId]);

    const project = getProject(projectId);
    const confirmAndDeleteProjectCallback = useConfirmAndDeleteOutlookSpace(
        project,
        onProjectDeleted
    );

    const editBoardTitle = loc(editBoardAriaLabel, text);
    const deleteBoardTitle = loc(deleteBoardAriaLabel, text);

    return (
        <div
            className={styles.menuItemRoot}
            title={title}
            onClick={onBoardClicked}
            data-is-focusable={true}
            onMouseEnter={showEditTools}
            onMouseLeave={hideEditTools}
            onFocus={showEditTools}
            onBlur={hideEditTools}>
            <div className={styles.checkContainer}>
                {checked && <Icon iconName={ControlIcons.CheckMark} />}
            </div>
            <div className={styles.boardNameText}>{text}</div>
            {shouldShowEditTools && (
                <div className={styles.editBoardContainer}>
                    {defaultProject?.projectId !== projectId && (
                        <>
                            <IconButton
                                ariaLabel={editBoardTitle}
                                title={editBoardTitle}
                                className={styles.editBoardButtons}
                                onClick={onEditBoardClicked}
                                iconProps={{ iconName: ControlIcons.Edit }}
                            />
                            <IconButton
                                ariaLabel={deleteBoardTitle}
                                title={deleteBoardTitle}
                                className={styles.editBoardButtons}
                                onClick={confirmAndDeleteProjectCallback}
                                iconProps={{ iconName: ControlIcons.Delete }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
});
